import React from "react";
import { Building2, GraduationCap, Target, Users, UserCheck } from "lucide-react";
import BgOrbs from "./BgOrbs";

const IMPACTS = [
  {
    icon: Building2,
    role: "CHRO / HR Leaders",
    photo:
      "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=320&h=320&fit=crop&auto=format",
    outcomes: [
      "Clear visibility into training ROI",
      "Workforce capability development at scale",
    ],
    tone: "from-blue-500 to-indigo-500",
    bg: "from-blue-50 to-indigo-50 border-blue-100",
  },
  {
    icon: GraduationCap,
    role: "L&D Teams",
    photo:
      "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=320&h=320&fit=crop&auto=format",
    outcomes: [
      "Automated training management",
      "Higher engagement and effectiveness",
    ],
    tone: "from-violet-500 to-fuchsia-500",
    bg: "from-violet-50 to-fuchsia-50 border-violet-100",
  },
  {
    icon: Target,
    role: "Business Leaders",
    photo:
      "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=320&h=320&fit=crop&auto=format",
    outcomes: [
      "Improved employee performance",
      "Faster onboarding and skill readiness",
    ],
    tone: "from-teal-500 to-emerald-500",
    bg: "from-teal-50 to-emerald-50 border-teal-100",
  },
  {
    icon: Users,
    role: "Managers",
    photo:
      "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=320&h=320&fit=crop&auto=format",
    outcomes: [
      "Visibility into team progress and skills",
      "Better coaching conversations, backed by data",
    ],
    tone: "from-amber-500 to-orange-500",
    bg: "from-amber-50 to-orange-50 border-amber-100",
  },
  {
    icon: UserCheck,
    role: "Employees",
    photo:
      "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=320&h=320&fit=crop&auto=format",
    outcomes: [
      "Personalized, engaging learning experience",
      "Faster skill development that sticks",
    ],
    tone: "from-rose-500 to-pink-500",
    bg: "from-rose-50 to-pink-50 border-rose-100",
  },
];

export default function Impact() {
  return (
    <section
      id="impact"
      data-testid="impact-section"
      className="relative min-h-screen flex items-center py-24 sm:py-28 overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, #eff6ff 0%, #f5f3ff 50%, #ffffff 100%)",
      }}
    >
      <BgOrbs variant="violet" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-violet-700 px-3 py-1.5 rounded-full bg-violet-50 border border-violet-100">
            Organizational impact
          </div>
          <h2 className="mt-5 text-[34px] sm:text-[46px] leading-[1.08] font-extrabold tracking-tight text-slate-900 text-balance">
            Impact of AI Trainer across your organization.
          </h2>
          <p className="mt-5 text-[16.5px] leading-relaxed text-slate-600">
            From the boardroom to the frontline — every stakeholder wins when
            training becomes personal, measurable, and always on.
          </p>
        </div>

        {/* Layout: first card spans 2 cols, others fill rest */}
        <div className="mt-14 grid lg:grid-cols-6 gap-5">
          {IMPACTS.map((p, i) => {
            const Icon = p.icon;
            const isFeatured = i === 0;
            return (
              <article
                key={p.role}
                className={`card-lift relative overflow-hidden bg-gradient-to-br ${p.bg} border rounded-[22px] p-6 flex flex-col ${
                  isFeatured ? "lg:col-span-3 lg:row-span-2" : "lg:col-span-3"
                }`}
                data-testid={`impact-card-${i}`}
              >
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <img
                      src={p.photo}
                      alt={p.role}
                      loading="lazy"
                      className={`rounded-full object-cover border-2 border-white shadow-[0_8px_24px_-10px_rgba(15,23,42,0.35)] ${
                        isFeatured ? "w-20 h-20" : "w-14 h-14"
                      }`}
                    />
                    <div
                      className={`absolute -bottom-1 -right-1 flex items-center justify-center rounded-xl bg-gradient-to-br ${p.tone} text-white shadow-md ${
                        isFeatured ? "w-9 h-9" : "w-7 h-7"
                      }`}
                    >
                      <Icon size={isFeatured ? 18 : 14} strokeWidth={2.2} />
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
                      Role
                    </div>
                    <h3
                      className={`font-bold tracking-tight text-slate-900 ${
                        isFeatured ? "text-[22px]" : "text-[18px]"
                      }`}
                    >
                      {p.role}
                    </h3>
                  </div>
                </div>

                <ul className="mt-5 space-y-2.5 flex-1">
                  {p.outcomes.map((o) => (
                    <li
                      key={o}
                      className="flex items-start gap-2.5 text-[14px] text-slate-700 leading-snug"
                    >
                      <span
                        className={`mt-1 w-1.5 h-4 rounded-full bg-gradient-to-b ${p.tone} flex-shrink-0`}
                      />
                      <span>{o}</span>
                    </li>
                  ))}
                </ul>

                {isFeatured && (
                  <div className="mt-6 pt-5 border-t border-white/70">
                    <div className="text-[13px] italic text-slate-600 leading-relaxed">
                      “We finally have a real line of sight between training
                      investment and measurable workforce capability.”
                    </div>
                  </div>
                )}

                <div
                  className="absolute -right-10 -bottom-10 w-40 h-40 rounded-full opacity-40 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(circle, rgba(255,255,255,0.8), transparent 70%)",
                  }}
                />
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
